import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/*
 * Input variants mirror the prototype's field styles. The `state` variant
 * controls border and ring colour; `inputSize` controls height and font-size.
 *
 * We use `inputSize` rather than `size` to avoid colliding with the native
 * HTML `size` attribute (InputHTMLAttributes<HTMLInputElement> defines
 * `size?: number`). Anything visually new should be added here, not as
 * ad-hoc classes in feature code.
 */
export const inputVariants = cva(
  [
    'flex w-full bg-surface text-text',
    'border border-border-strong',
    'rounded-sm font-sans',
    'placeholder:text-text-dim',
    'transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    'file:border-0 file:bg-transparent file:font-sans file:text-sm',
  ].join(' '),
  {
    variants: {
      state: {
        default: 'border-border-strong focus-visible:border-accent',
        error:
          'border-danger focus-visible:ring-danger focus-visible:border-danger',
      },
      inputSize: {
        md: 'h-8 px-3 text-sm',
        lg: 'h-10 px-4 text-base',
      },
    },
    defaultVariants: {
      state: 'default',
      inputSize: 'md',
    },
  },
);

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, state, inputSize, ...props }, ref) => {
    return (
      <input
        ref={ref}
        className={cn(inputVariants({ state, inputSize }), className)}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';
